import Modal from "react-bootstrap/Modal";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";
import { saveEmailBody } from "../../store/actions/users/users.actions";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import TemplateSelectModal from "../TemplateSelectModal";

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 10px;
  }
`;

/** Escape plain text for safe insertion into HTML (contenteditable / innerHTML). */
export function escapeHtmlMergeValue(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/** Literal forms that may appear in HTML for a given merge value (raw + entity-escaped). */
function mergeValueHtmlLiterals(value) {
  if (value == null || value === "") return [];
  const raw = String(value);
  const escaped = escapeHtmlMergeValue(raw);
  const set = escaped === raw ? [raw] : [escaped, raw];
  return set.sort((a, b) => b.length - a.length);
}

// Styled component for the clickable columns
export function applyEmailTemplateMergeFields(
  text,
  { projectName, companyNameMerge, prospectCompanyName, recipientFirstName, yourName },
  { escape = true } = {}
) {
  if (text == null || text === "") return "";
  const format = (value) => {
    if (value == null || value === "") return "";
    return escape ? escapeHtmlMergeValue(value) : String(value);
  };
  return String(text)
    .replaceAll("[prospect company name]", format(prospectCompanyName))
    .replaceAll("[project name]", format(projectName))
    .replaceAll("[company name]", format(companyNameMerge))
    .replaceAll("[name]", format(recipientFirstName))
    .replaceAll("[your name]", format(yourName));
}

export function stripEmailTemplateMergeFields(body, { projectName, companyNameMerge, prospectCompanyName, recipientFirstName, yourName }) {
  const pairs = [
    [projectName, "[project name]"],
    [prospectCompanyName, "[prospect company name]"],
    [companyNameMerge, "[company name]"],
    [recipientFirstName, "[name]"],
    [yourName, "[your name]"],
  ].filter(([val]) => val != null && String(val) !== "");
  pairs.sort((a, b) => String(b[0]).length - String(a[0]).length);
  let s = body;
  for (const [val, token] of pairs) {
    for (const literal of mergeValueHtmlLiterals(val)) {
      s = s.replaceAll(literal, token);
    }
  }
  return s;
}

const StyledCol = styled(Col)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin: 5px;
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s, box-shadow 0.2s;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  flex-grow: 1;

  &:hover {
    background-color: #f0f0f0;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-3px);
  }

  &:active {
    background-color: #e0e0e0;
    box-shadow: inset 0px 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(0);
  }
`;

const EmailModal = (props) => {
  const { user: profile } = useSelector((state) => state.userDetails);
  const { loading, error, body } = useSelector(
    (state) => state.emailBody
  );
  const [emailBody, setEmailBody] = useState();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [subjectRaw, setSubjectRaw] = useState("");
  const editorBodyRef = useRef(null);
  const templateNameRef = useRef(null);
  const dispatch = useDispatch();
  const {
    app,
    projectName,
    company_name,
    accountant_name,
    prospect_company_name,
    recipientFirstName,
    emailTemplates,
    onSelectTemplate,
    onSaveTemplate,
    emailTemplateBody,
  } = props;
  const companyNameMerge = accountant_name ?? company_name ?? "";
  const prospectCompanyName = prospect_company_name ?? "";
  const yourName = profile?.first_name ?? "";
  const isUserEditableTemplates = emailTemplates?.length > 0 && emailTemplates[0]?.key != null;

  const mergeFields = useCallback(
    () => ({
      projectName,
      companyNameMerge,
      prospectCompanyName,
      recipientFirstName,
      yourName,
    }),
    [projectName, companyNameMerge, prospectCompanyName, recipientFirstName, yourName]
  );

  const withPlaceholders = useCallback(
    (text) => applyEmailTemplateMergeFields(text, mergeFields()),
    [mergeFields]
  );

  const subjectPreview = applyEmailTemplateMergeFields(subjectRaw, mergeFields(), {
    escape: false,
  });
  const subjectTemplateName =
    (emailTemplates?.find((template) => template.key === selectedTemplateKey) || emailTemplates?.[0])
      ?.name || "";

  useEffect(() => {
    let resolvedBody;
    if (emailTemplateBody?.text) {
      resolvedBody = withPlaceholders(emailTemplateBody.text);
      setEmailBody(resolvedBody);
    } else if (app === "project_board" && profile?.project_email_body) {
      resolvedBody = withPlaceholders(profile.project_email_body);
      setEmailBody(resolvedBody);
    } else if (app === "contact_outreach" && profile?.contact_email_body) {
      resolvedBody = withPlaceholders(profile.contact_email_body);
      setEmailBody(resolvedBody);
    } else if (app === "crm" && profile?.crm_email_body) {
      resolvedBody = withPlaceholders(profile.crm_email_body);
      setEmailBody(resolvedBody);
    } else if (app === "proposal" && profile?.proposal_email_body) {
      resolvedBody = withPlaceholders(profile.proposal_email_body);
      setEmailBody(resolvedBody);
    } else {
      resolvedBody = emailTemplates?.length > 0 ? withPlaceholders(emailTemplates[0]?.body ?? "") : "";
      setEmailBody(resolvedBody);
    }
    if (emailTemplates?.length > 0 && emailTemplates[0]?.key != null) {
      const match = emailTemplates.find(
        (t) => withPlaceholders(t?.body) === resolvedBody || t?.body === resolvedBody
      );
      if (match) {
        setSelectedTemplateKey(match.key);
        setSelectedTemplateName(match.name || "");
      } else {
        setSelectedTemplateKey(null);
        setSelectedTemplateName("");
      }
    }
  }, [
    app,
    withPlaceholders,
    profile?.project_email_body,
    profile?.proposal_email_body,
    profile?.contact_email_body,
    profile?.crm_email_body,
    emailTemplateBody?.text,
    // Intentionally omit emailTemplates so refetch after save doesn't change selection
  ])

  useEffect(() => {
    if (!loading && !error && body) {
      setEmailBody(withPlaceholders(body));
    }
  }, [body, loading, error, withPlaceholders]);

  useEffect(() => {
    if (!isUserEditableTemplates) {
      setSubjectRaw("");
      return;
    }
    let selected = emailTemplates.find((template) => template.key === selectedTemplateKey);
    if (!selected && props.show) {
      selected = emailTemplates.find(
        (template) =>
          withPlaceholders(template?.body) === emailBody || template?.body === emailBody
      );
      if (selected) {
        setSelectedTemplateKey(selected.key);
        setSelectedTemplateName(selected.name || "");
      }
    }
    const template = selected || emailTemplates[0];
    setSubjectRaw(template?.subject || "");
  }, [
    isUserEditableTemplates,
    selectedTemplateKey,
    emailTemplates,
    props.show,
    emailBody,
    withPlaceholders,
  ]);

  const saveTemplateAndBody = () => {
    const bodyEl = editorBodyRef.current;
    const nameEl = templateNameRef.current;
    if (!bodyEl) return null;
    const body = bodyEl.innerHTML;
    setEmailBody(body);
    const bodyForProfile = stripEmailTemplateMergeFields(body, {
      projectName,
      companyNameMerge,
      prospectCompanyName,
      recipientFirstName,
      yourName,
    });
    if (isUserEditableTemplates && selectedTemplateKey && onSaveTemplate) {
      const name = nameEl?.textContent?.trim() ?? selectedTemplateName;
      onSaveTemplate(selectedTemplateKey, bodyForProfile, name);
    }
    dispatch(saveEmailBody(bodyForProfile, app === "crm" ? "crm" : app === "project_board" ? "project" : app === "contact_outreach" ? "contact" : "proposal"));
    return { rawHtml: body, storageHtml: bodyForProfile };
  };

  const handleAppSelection = (appName) => {
    const saved = saveTemplateAndBody();
    const subjectForSend = applyEmailTemplateMergeFields(subjectRaw, mergeFields(), {
      escape: false,
    });
    if (props.onChange) {
      props.onChange(appName, saved?.rawHtml ?? emailBody, saved?.storageHtml, subjectForSend);
    }
  };

  const handleChangeBody = (e) => {
    setEmailBody(e.target.innerHTML)
  }


  const handlePaste = (e) => {
    e.preventDefault(); // Prevent the default paste behavior

    // Get the plain text from clipboard
    const text = e.clipboardData.getData('text/plain');

    // Insert the plain text at the cursor position
    document.execCommand('insertText', false, text);
  }

  return (
    <StyledModal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton={props.closeButton}>
        <Modal.Title id="contained-modal-title-vcenter">
          Choose Email App
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {app == "proposal" && (
          <Row className="d-flex justify-content-between">
            <Col>
              <h3>
                To complete your proposal submission, include a message in your
                email body and attach the proposal document.
              </h3>
            </Col>
          </Row>
        )}
        <Row className="d-flex justify-content-between">
          <StyledCol
            md={2}
            xs={6}
            onClick={() => handleAppSelection("default")}
          >
            Default
          </StyledCol>
          <StyledCol md={2} xs={6} onClick={() => handleAppSelection("gmail")}>
            Gmail
          </StyledCol>
          <StyledCol
            md={2}
            xs={6}
            onClick={() => handleAppSelection("outlook")}
          >
            Outlook
          </StyledCol>
          <StyledCol md={2} xs={6} onClick={() => handleAppSelection("yahoo")}>
            Yahoo Mail
          </StyledCol>
        </Row>
        {/* {app == "project_board" &&
          <> */}
            <Row className="d-flex justify-content-between mt-4">
              <Row className="mb-2 pb-1 text-center">
                <Col className="text-muted" sm={12}>
                  Available Merge Fields:{" "}
                  {[ "[project name]", "[company name]", "[prospect company name]", "[name]", "[your name]" ].join(", ")}
                </Col>
                <Col className="text-muted" sm={12}>
                  Copy and paste the merge field value into your template below.
                </Col>
              </Row>
              <Col className="d-flex align-items-center flex-wrap gap-2">
                <h3 className="mb-0">Email Template Edit</h3>
                {emailTemplates?.length > 0 && (
                  <>
                    <TemplateSelectModal
                      templates={emailTemplates}
                      onSelect={(template) => {
                        if (isUserEditableTemplates && template.body != null) {
                          setEmailBody(withPlaceholders(template.body));
                          setSelectedTemplateKey(template.key);
                          setSelectedTemplateName(template.name || "");
                          setSubjectRaw(template.subject || "");
                        } else {
                          onSelectTemplate?.(template.id);
                        }
                      }}
                    />
                    {isUserEditableTemplates && (
                      <Link
                        to="/email_templates"
                        className="ms-2"
                        style={{ fontSize: 14 }}
                      >
                        Manage templates
                      </Link>
                    )}
                  </>
                )}
              </Col>
            </Row>
            <Row className="mx-4 pt-2">
              {isUserEditableTemplates && (
                <div className="mb-2 w-100">
                  <div className="text-muted fs-4">
                    Subject{subjectTemplateName ? ` — ${subjectTemplateName}` : ""}
                  </div>
                  <div
                    className="border rounded p-2"
                    style={{ fontSize: "15px", background: "#f8f9fa" }}
                  >
                    {subjectPreview || "—"}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                    Edit the subject on the Email Templates page.
                  </div>
                </div>
              )}
              {isUserEditableTemplates && selectedTemplateKey && (
                <div className="mb-2 w-100 d-flex gap-2 align-items-center" key={selectedTemplateKey}>
                  <div className="text-muted fs-4">Template Name:</div>
                  <div
                    ref={templateNameRef}
                    style={{ fontSize: "15px", width: "84%" }}
                    className="editor_template_name border rounded p-2"
                    contentEditable={true}
                    suppressContentEditableWarning
                    onBlur={(e) => setSelectedTemplateName(e.target.textContent?.trim() ?? "")}
                  >
                    {selectedTemplateName}
                  </div>
                </div>
              )}
              <div
                ref={editorBodyRef}
                style={{ fontSize: "15px" }}
                className="editor_email_body border rounded p-2"
                contentEditable={true}
                dangerouslySetInnerHTML={{
                  __html: emailBody,
                }}
                onPaste={handlePaste}
                onBlur={handleChangeBody}
              />
              <div className="d-flex mt-3 justify-content-end gap-2 align-items-center flex-wrap">
                {isUserEditableTemplates && selectedTemplateKey && onSaveTemplate && (
                  <Button variant="primary" onClick={saveTemplateAndBody}>
                    Save template
                  </Button>
                )}
              </div>
            </Row>
          {/* </>
        } */}
      </Modal.Body>
    </StyledModal>
  );
};

EmailModal.defaultProps = {
  closeButton: false,
};

export default EmailModal;
